/**
 * useRefreshData - Centralized data refresh hook.
 * 
 * After any mutation (create/update/delete of events, activities, tours, etc.),
 * call refreshData() to invalidate all cached queries so the UI reflects the latest state.
 * 
 * Pass an artistId to also emit a change signal to other users via Firestore:
 *   refreshData(artistId)  →  local invalidation + cross-user notification
 *   refreshData()          →  local invalidation only (backward-compatible)
 * 
 * Usage:
 *   const { refreshData, refreshEvents, refreshActivities } = useRefreshData();
 *   
 *   await createEvent(dataConnect, { ... });
 *   refreshData(artistId); // Invalidates locally AND notifies other users
 *   
 *   // Or more targeted:
 *   await updateActivity(dataConnect, { ... });
 *   refreshActivities(artistId); // Only invalidates activity-related queries + signal
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Logger from '../utils/Logger';
import { emitChangeSignal } from './useChangeSignal';

// All query key prefixes used in ArtistContext
const QUERY_KEYS = {
    // Core data
    ARTISTS: 'artists',
    EVENTS: 'events',
    EVENT: 'event',
    ACTIVITY: 'activity',
    
    // Calendar / date-ranged queries
    ARTISTS_CALENDAR: 'artists-calendar',
    ARTISTS_EVENTS_BASIC: 'artists-events-basic',
    ARTISTS_ACTIVITIES_DETAILED: 'artists-activities-detailed',
    
    // Tours
    TOURS: 'tours',
    TOUR: 'tour',
    
    // Artist
    ARTIST: 'artist',
};

const useRefreshData = () => {
    const queryClient = useQueryClient();

    /** Invalidate ALL cached data — nuclear option, use after cross-cutting changes */
    const refreshData = useCallback((artistId) => {
        Logger.debug('useRefreshData', 'Invalidating ALL queries');
        queryClient.invalidateQueries({ refetchType: 'all' });
        if (artistId) emitChangeSignal(artistId, 'all');
    }, [queryClient]);

    /** Invalidate event-related queries (events list, calendar, individual events) */
    const refreshEvents = useCallback((artistId) => {
        Logger.debug('useRefreshData', 'Invalidating event queries');
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENTS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENT], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_CALENDAR], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_EVENTS_BASIC], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_ACTIVITIES_DETAILED], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOURS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOUR], refetchType: 'all' });
        if (artistId) emitChangeSignal(artistId, 'events');
    }, [queryClient]);

    /** Invalidate activity-related queries */
    const refreshActivities = useCallback((artistId) => {
        Logger.debug('useRefreshData', 'Invalidating activity queries');
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVITY], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_CALENDAR], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_ACTIVITIES_DETAILED], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENT], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENTS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS], refetchType: 'all' });
        if (artistId) emitChangeSignal(artistId, 'activities');
    }, [queryClient]);

    /** Invalidate tour-related queries */
    const refreshTours = useCallback((artistId) => {
        Logger.debug('useRefreshData', 'Invalidating tour queries');
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOURS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TOUR], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_EVENTS_BASIC], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENTS], refetchType: 'all' });
        if (artistId) emitChangeSignal(artistId, 'tours');
    }, [queryClient]);

    /** Invalidate a specific event by ID */
    const refreshEvent = useCallback((eventId, artistId) => {
        Logger.debug('useRefreshData', `Invalidating event ${eventId}`);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENT, eventId], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EVENTS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_CALENDAR], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS_EVENTS_BASIC], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS], refetchType: 'all' });
        if (artistId) emitChangeSignal(artistId, 'events');
    }, [queryClient]);

    /** Invalidate artist-related queries */
    const refreshArtists = useCallback((artistId) => {
        Logger.debug('useRefreshData', 'Invalidating artist queries');
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTISTS], refetchType: 'all' });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ARTIST], refetchType: 'all' });
        if (artistId) emitChangeSignal(artistId, 'crew');
    }, [queryClient]);

    return {
        refreshData,
        refreshEvents,
        refreshActivities,
        refreshTours,
        refreshEvent,
        refreshArtists,
        QUERY_KEYS,
    };
};

export { QUERY_KEYS };
export default useRefreshData;
